/**
 * Language pickers (docs/I18N.md): a compact menu for the landing screen and a segmented list for Settings. Both show
 * every AVAILABLE locale by its native name (English, Tiếng Việt, 简体中文…) with a matching `lang` attribute so the
 * right font is used. Choosing one loads the locale's dictionaries + content catalog first (no flash of English),
 * then saves it on the device, on the active child's profile (synced) and on the account (server emails / push).
 */
import { enqueueSync } from '../lib/syncQueue';
import { useEffect, useRef, useState } from 'react';
import { AVAILABLE_LOCALES, LOCALES, setProfilePref, type Locale } from '@sketchling/core';
import { useApp } from '../state/store';
import { loadLocale, translate, useLang, useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { api } from '../lib/api';
import { Icon } from './icons';
import { toast } from './ui';

/** Switch the app language (device + active profile + account). */
export async function setAppLanguage(l: Locale, opts: { toast?: boolean } = {}): Promise<void> {
  await loadLocale(l).catch(() => undefined);
  const st = useApp.getState();
  st.updateSettings({ language: l });
  if (st.activeProfileId) st.updateCollect(st.activeProfileId, (c) => setProfilePref(c, 'locale', l, new Date().toISOString()));
  if (st.account.accessToken) enqueueSync('lang', 'lang', l, 300);
  if (opts.toast) toast(translate(l, 'set.langSwitched'));
}

/** Landing: globe chip with the current language → a menu of native names. */
export function LanguageMenu({ testId = 'landing-lang' }: { testId?: string }) {
  const lang = useLang();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('pointerdown', off);
    return () => document.removeEventListener('pointerdown', off);
  }, [open]);
  return (
    <div className="lang-menu" ref={ref}>
      <button type="button" className="chip lang-menu-btn" aria-haspopup="menu" aria-expanded={open} aria-label={t('set.language')} onClick={() => (play('click'), setOpen(!open))} data-testid={testId}>
        <Icon name="globe" size={18} />
        <span lang={lang}>{LOCALES[lang].native}</span>
      </button>
      {open && (
        <ul className="lang-menu-list paper-card" role="menu" data-testid="lang-menu">
          {AVAILABLE_LOCALES.map((l) => (
            <li key={l}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={l === lang}
                lang={l}
                className={l === lang ? 'on' : ''}
                onClick={() => {
                  play('slider');
                  setOpen(false);
                  void setAppLanguage(l);
                }}
                data-testid={`lang-opt-${l}`}
              >
                {LOCALES[l].native}
                {l === lang && <Icon name="check" size={16} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Settings: one button per available language (native names). */
export function LanguageSegment() {
  const lang = useLang();
  const t = useT();
  return (
    <div className="mini-seg lang-seg" role="group" aria-label={t('set.language')}>
      {AVAILABLE_LOCALES.map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          className={lang === l ? 'on' : ''}
          aria-pressed={lang === l}
          data-testid={`set-lang-${l}`}
          onClick={() => {
            play('slider');
            void setAppLanguage(l, { toast: true });
          }}
        >
          {LOCALES[l].native}
        </button>
      ))}
    </div>
  );
}
